/**
 * Renderer for Voronoi (polygon-based) mazes.
 *
 * Draws irregular polygon cells generated from a Voronoi tessellation
 * inside a silhouette shape. Renders contour outline, wall edges between
 * non-connected regions, user path, and entrance/exit emojis.
 * Supports dark/light mode and PNG/SVG export.
 */

import type {
  VoronoiMazeState,
  VoronoiMazeData,
  VoronoiRegion,
  PathSegment,
  Position,
} from '../types/maze-types';
import { MAZE_COLORS, GAME_COLORS, getMazeBackground, getZoneColors } from '@/contexts/games/constants/colors';
import { MAZE_EMOJIS } from '../config/mazeEmojis';
import { computeBadgePlacement, computeCentroid } from './voronoiEmojiPosition';

/** An edge between two vertices, shared by one or two regions. */
interface EdgeInfo {
  v1: Position;
  v2: Position;
  regionIds: number[];
}

/**
 * Create a canonical string key for a vertex (rounded to 1 decimal).
 */
function vertexKey(p: Position): string {
  return `${Math.round(p.x * 10)},${Math.round(p.y * 10)}`;
}

/**
 * Create a canonical key for an edge (order-independent).
 */
function edgeKey(a: Position, b: Position): string {
  const ka = vertexKey(a);
  const kb = vertexKey(b);
  return ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`;
}

/**
 * Compute the midpoint of an edge.
 */
function edgeMidpoint(v1: Position, v2: Position): Position {
  return { x: (v1.x + v2.x) / 2, y: (v1.y + v2.y) / 2 };
}

/**
 * Squared distance between two points.
 */
function distSq(a: Position, b: Position): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}

/**
 * Build edge maps from Voronoi regions.
 * Returns wall edges (shared by 2 non-connected regions) and
 * contour edges (belonging to only 1 region).
 *
 * After the initial vertex-matching pass, a second pass ensures that
 * every neighbor pair (passage) has no wall drawn between them. When
 * Voronoi clipping causes vertex rounding mismatches, the shared edge
 * may not be detected — the second pass finds and removes the closest
 * wall/contour edge for each unmatched neighbor pair.
 */
function classifyEdges(mazeData: VoronoiMazeData): {
  wallEdges: Array<[Position, Position]>;
  contourEdges: Array<[Position, Position]>;
} {
  const neighborSet = new Set<string>();
  const edgeMap = new Map<string, EdgeInfo>();

  // Build neighbor pair set (passages — no wall)
  for (const region of mazeData.regions) {
    for (const nid of region.neighbors) {
      const a = Math.min(region.id, nid);
      const b = Math.max(region.id, nid);
      neighborSet.add(`${a},${b}`);
    }
  }

  // Build regionById lookup for the second pass
  const regionById = new Map<number, VoronoiRegion>();
  for (const region of mazeData.regions) {
    regionById.set(region.id, region);
  }

  // Collect all edges from all regions
  for (const region of mazeData.regions) {
    const verts = region.vertices;
    for (let i = 0; i < verts.length; i++) {
      const v1 = verts[i];
      const v2 = verts[(i + 1) % verts.length];
      const key = edgeKey(v1, v2);

      const existing = edgeMap.get(key);
      if (existing) {
        if (!existing.regionIds.includes(region.id)) {
          existing.regionIds.push(region.id);
        }
      } else {
        edgeMap.set(key, { v1, v2, regionIds: [region.id] });
      }
    }
  }

  // Track which neighbor pairs were matched by vertex-key matching
  const matchedNeighborPairs = new Set<string>();

  const wallEdges: Array<[Position, Position]> = [];
  const contourEdges: Array<[Position, Position]> = [];

  for (const info of edgeMap.values()) {
    if (info.regionIds.length === 1) {
      // Boundary edge — part of contour outline
      contourEdges.push([info.v1, info.v2]);
    } else if (info.regionIds.length === 2) {
      const a = Math.min(info.regionIds[0], info.regionIds[1]);
      const b = Math.max(info.regionIds[0], info.regionIds[1]);
      const pairKey = `${a},${b}`;
      if (neighborSet.has(pairKey)) {
        // Passage — skip drawing
        matchedNeighborPairs.add(pairKey);
      } else {
        // Shared edge with no passage — wall
        wallEdges.push([info.v1, info.v2]);
      }
    }
  }

  // --- Second pass: fix unmatched neighbor pairs ---
  // For neighbor pairs whose shared edge was NOT found by vertex matching
  // (due to rounding), find and remove the closest wall/contour edges
  // so that the passage is visually open.
  for (const pairKey of neighborSet) {
    if (matchedNeighborPairs.has(pairKey)) continue;

    const [aStr, bStr] = pairKey.split(',');
    const regionA = regionById.get(Number(aStr));
    const regionB = regionById.get(Number(bStr));
    if (!regionA || !regionB) continue;

    // Compute centroid of each region for proximity checks
    const centroidB = computeCentroid(regionB);
    const centroidA = computeCentroid(regionA);

    // Find edge of A closest to B's centroid and remove it from walls/contour
    removeClosestEdge(regionA, centroidB, wallEdges, contourEdges);
    // Find edge of B closest to A's centroid and remove it from walls/contour
    removeClosestEdge(regionB, centroidA, wallEdges, contourEdges);
  }

  // (No "widen narrow passages" pass any more: the carve itself never opens a
  // passage narrower than MIN_PASSAGE_PX — see mazeRecarver.pruneNarrowPassages —
  // so the walls drawn here are exactly the walls the path collision uses.)

  return { wallEdges, contourEdges };
}

/**
 * Find the edge of `region` whose midpoint is closest to `target`, then
 * remove it from wallEdges or contourEdges if present.
 */
function removeClosestEdge(
  region: VoronoiRegion,
  target: Position,
  wallEdges: Array<[Position, Position]>,
  contourEdges: Array<[Position, Position]>,
): void {
  const verts = region.vertices;
  if (verts.length === 0) return;

  let bestDist = Infinity;
  let bestKey = '';

  for (let i = 0; i < verts.length; i++) {
    const v1 = verts[i];
    const v2 = verts[(i + 1) % verts.length];
    const mid = edgeMidpoint(v1, v2);
    const d = distSq(mid, target);
    if (d < bestDist) {
      bestDist = d;
      bestKey = edgeKey(v1, v2);
    }
  }

  if (!bestKey) return;

  // Remove from wallEdges
  for (let i = wallEdges.length - 1; i >= 0; i--) {
    if (edgeKey(wallEdges[i][0], wallEdges[i][1]) === bestKey) {
      wallEdges.splice(i, 1);
    }
  }
  // Remove from contourEdges
  for (let i = contourEdges.length - 1; i >= 0; i--) {
    if (edgeKey(contourEdges[i][0], contourEdges[i][1]) === bestKey) {
      contourEdges.splice(i, 1);
    }
  }
}

export class VoronoiMazeRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;

  /** Cached edge classification — invalidated when mazeData identity changes. */
  private cachedMazeData: VoronoiMazeData | null = null;
  private cachedWallEdges: Array<[Position, Position]> = [];
  private cachedContourEdges: Array<[Position, Position]> = [];

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Cannot get 2D context from canvas');
    }
    this.ctx = ctx;
  }

  /**
   * Return (and cache) classified edges for the given mazeData.
   * Re-computes only when mazeData reference changes.
   */
  private getClassifiedEdges(mazeData: VoronoiMazeData): {
    wallEdges: Array<[Position, Position]>;
    contourEdges: Array<[Position, Position]>;
  } {
    if (mazeData !== this.cachedMazeData) {
      const result = classifyEdges(mazeData);
      this.cachedMazeData = mazeData;
      this.cachedWallEdges = result.wallEdges;
      this.cachedContourEdges = result.contourEdges;
    }
    return { wallEdges: this.cachedWallEdges, contourEdges: this.cachedContourEdges };
  }

  /**
   * Full render of the Voronoi maze.
   */
  render(state: VoronoiMazeState, includePath = true): void {
    this.clearCanvas(state);

    const { wallEdges, contourEdges } = this.getClassifiedEdges(state.mazeData);
    const contour = state.mazeData.contour;

    // Draw source image as semi-transparent background (clipped to contour)
    if (state.backgroundImage && contour && contour.length > 2) {
      const { ctx } = this;
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(contour[0].x, contour[0].y);
      for (let i = 1; i < contour.length; i++) {
        ctx.lineTo(contour[i].x, contour[i].y);
      }
      ctx.closePath();
      ctx.clip();
      // Draw image matching exactly how the worker processed it:
      // Worker: resize preserving aspect ratio → center on square canvas → normalize to 0..1.
      // Client: scales 0..1 → (coord * usable + pad).
      // So image must be drawn with the same resize + center, mapped to client coords.
      const img = state.backgroundImage;
      const imgAspect = img.naturalWidth / img.naturalHeight;
      // The worker fit the photo into its SQUARE canvas (aspect kept, centred) and
      // normalized by that square: in 0..1 space the photo occupies nw × nh at
      // ((1-nw)/2, (1-nh)/2). Map that rectangle with the same affine transform
      // fitGeometryToBox applied to the cells, so the photo sits exactly under them.
      const nw = imgAspect >= 1 ? 1 : imgAspect;
      const nh = imgAspect >= 1 ? 1 / imgAspect : 1;
      const nx = (1 - nw) / 2;
      const ny = (1 - nh) / 2;
      let drawX: number, drawY: number, drawW: number, drawH: number;
      const tf = state.mazeData.geometryTransform;
      if (tf) {
        drawX = nx * tf.scale + tf.ox;
        drawY = ny * tf.scale + tf.oy;
        drawW = nw * tf.scale;
        drawH = nh * tf.scale;
      } else {
        // legacy square mapping (pad 20, usable = min side − 40)
        const pad = 20;
        const usable = Math.min(state.canvasSize.width, state.canvasSize.height) - pad * 2;
        drawX = pad + nx * usable;
        drawY = pad + ny * usable;
        drawW = nw * usable;
        drawH = nh * usable;
      }

      ctx.globalAlpha = Math.max(0, Math.min(1, state.backgroundOpacity ?? 0.3));
      ctx.drawImage(img, drawX, drawY, drawW, drawH);
      ctx.globalAlpha = 1.0;
      ctx.restore();
    }

    // Entry / exit cell tints go UNDER the walls: a filled cell says "this room"
    // while every wall of the room stays crisp and every opening stays open
    // (a tint or ring drawn over the walls hid which edges were real).
    this.drawZoneTints(state);

    // Draw ALL walls and contour edges (no clip)
    this.drawWalls(wallEdges, state);
    this.drawContour(contourEdges, state);
    // …then re-stroke the entry / exit ROOM'S OWN walls in the room colour: on a
    // photo the tint alone gets lost, and a coloured ring hid the openings — this
    // colours exactly the wall segments that exist, so every gap stays a gap.
    this.drawZoneWallHighlights(wallEdges, contourEdges, state);

    if (includePath) {
      this.drawPath(state);
    }

    // Mask everything outside the contour with background color.
    // This hides Voronoi edges that extend beyond the silhouette
    // WITHOUT blocking passages (unlike clip which cuts through passages).
    if (contour && contour.length > 2) {
      const { ctx } = this;
      const bgColor = getMazeBackground(state.isDarkMode ?? false);

      ctx.save();
      ctx.beginPath();
      // Draw a large rectangle covering the entire canvas
      ctx.rect(0, 0, this.canvas.width, this.canvas.height);
      // Cut out the contour shape (even-odd rule: inside contour = transparent)
      ctx.moveTo(contour[0].x, contour[0].y);
      for (let i = 1; i < contour.length; i++) {
        ctx.lineTo(contour[i].x, contour[i].y);
      }
      ctx.closePath();
      ctx.fillStyle = bgColor;
      ctx.fill('evenodd');
      ctx.restore();

      // Draw contour outline ON TOP of the even-odd mask
      // This gives the silhouette a clean border without blocking passages
      // (passages are gaps in contourEdges, not in this outline)
      ctx.strokeStyle = state.isDarkMode
        ? MAZE_COLORS.wallDark
        : MAZE_COLORS.wallLight;
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(contour[0].x, contour[0].y);
      for (let i = 1; i < contour.length; i++) {
        ctx.lineTo(contour[i].x, contour[i].y);
      }
      ctx.closePath();
      ctx.stroke();
    }

    this.drawZoneMarkers(state);
  }

  /**
   * Re-render only the path layer on top of the existing maze.
   * Redraws the full canvas from the cached edges (walls + contour)
   * but avoids re-computing classifyEdges, making it much faster
   * during interactive drawing.
   */
  renderPathOnly(state: VoronoiMazeState): void {
    // Full re-render using cached edges (classifyEdges is skipped via cache)
    this.render(state, true);
  }

  /**
   * Create a PNG data URL for download (without user path).
   */
  createDownloadImage(state: VoronoiMazeState): string {
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = this.canvas.width;
    tempCanvas.height = this.canvas.height;

    const tempRenderer = new VoronoiMazeRenderer(tempCanvas);
    tempRenderer.render(state, false);

    return tempCanvas.toDataURL('image/png');
  }

  // ---------------------------------------------------------------------------
  // Canvas clear
  // ---------------------------------------------------------------------------

  private clearCanvas(state: VoronoiMazeState): void {
    this.ctx.fillStyle = getMazeBackground(state.isDarkMode ?? false);
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
  }

  // ---------------------------------------------------------------------------
  // Contour outline (silhouette boundary)
  // ---------------------------------------------------------------------------

  private drawContour(
    contourEdges: Array<[Position, Position]>,
    state: VoronoiMazeState,
  ): void {
    const { ctx } = this;

    ctx.strokeStyle = state.isDarkMode
      ? MAZE_COLORS.wallDark
      : MAZE_COLORS.wallLight;
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Don't draw the silhouette contour as a closed polygon — it blocks
    // passages on the border. Instead, use contourEdges (Voronoi boundary
    // edges) which naturally have gaps where passages exist.

    // Fallback: draw boundary edges
    for (const [v1, v2] of contourEdges) {
      ctx.beginPath();
      ctx.moveTo(v1.x, v1.y);
      ctx.lineTo(v2.x, v2.y);
      ctx.stroke();
    }
  }

  // ---------------------------------------------------------------------------
  // Wall drawing (edges between non-connected regions)
  // ---------------------------------------------------------------------------

  private drawWalls(
    wallEdges: Array<[Position, Position]>,
    state: VoronoiMazeState,
  ): void {
    const { ctx } = this;

    ctx.strokeStyle = state.isDarkMode
      ? MAZE_COLORS.wallDark
      : MAZE_COLORS.wallLight;
    ctx.lineWidth = 1.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    for (const [v1, v2] of wallEdges) {
      ctx.beginPath();
      ctx.moveTo(v1.x, v1.y);
      ctx.lineTo(v2.x, v2.y);
      ctx.stroke();
    }
  }

  // ---------------------------------------------------------------------------
  // Path drawing
  // ---------------------------------------------------------------------------

  private drawPath(state: VoronoiMazeState): void {
    state.pathSegments.forEach((segment: PathSegment) => {
      if (segment.points.length < 2) return;

      this.ctx.strokeStyle = segment.color;
      this.ctx.lineWidth = 3;
      this.ctx.lineCap = 'round';
      this.ctx.lineJoin = 'round';
      this.ctx.beginPath();

      this.ctx.moveTo(segment.points[0].x, segment.points[0].y);
      for (let i = 1; i < segment.points.length; i++) {
        this.ctx.lineTo(segment.points[i].x, segment.points[i].y);
      }
      this.ctx.stroke();
    });
  }

  // ---------------------------------------------------------------------------
  // Entrance / Exit zones
  // ---------------------------------------------------------------------------

  private zoneRegions(state: VoronoiMazeState): { entry?: VoronoiRegion; exit?: VoronoiRegion } {
    const { mazeData } = state;
    return {
      entry: mazeData.regions.find((r) => r.id === mazeData.entryRegionId),
      exit: mazeData.regions.find((r) => r.id === mazeData.exitRegionId),
    };
  }

  /** Fill the entry / exit cells (clipped to the silhouette). Drawn BEFORE walls. */
  private drawZoneTints(state: VoronoiMazeState): void {
    const { entry, exit } = this.zoneRegions(state);
    const contour = state.mazeData.contour;
    const { ctx } = this;
    const fill = (region: VoronoiRegion | undefined, color: string) => {
      if (!region || region.vertices.length < 3) return;
      ctx.save();
      if (contour && contour.length > 2) {
        ctx.beginPath();
        ctx.moveTo(contour[0].x, contour[0].y);
        for (let i = 1; i < contour.length; i++) ctx.lineTo(contour[i].x, contour[i].y);
        ctx.closePath();
        ctx.clip();
      }
      ctx.beginPath();
      ctx.moveTo(region.vertices[0].x, region.vertices[0].y);
      for (let i = 1; i < region.vertices.length; i++) ctx.lineTo(region.vertices[i].x, region.vertices[i].y);
      ctx.closePath();
      ctx.fillStyle = color;
      ctx.fill();
      ctx.restore();
    };
    const zc = getZoneColors(state.isDarkMode ?? false);
    fill(entry, zc.entryTint);
    fill(exit, zc.exitTint);
  }

  /** Stroke the wall / contour edges that belong to the entry / exit cells in the zone colour. */
  private drawZoneWallHighlights(
    wallEdges: Array<[Position, Position]>,
    contourEdges: Array<[Position, Position]>,
    state: VoronoiMazeState,
  ): void {
    const { entry, exit } = this.zoneRegions(state);
    const { ctx } = this;
    const highlight = (region: VoronoiRegion | undefined, color: string) => {
      if (!region || region.vertices.length < 3) return;
      const own = new Set<string>();
      const v = region.vertices;
      for (let i = 0; i < v.length; i++) own.add(edgeKey(v[i], v[(i + 1) % v.length]));
      ctx.save();
      ctx.strokeStyle = color;
      ctx.lineWidth = 4;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      for (const [a, b] of wallEdges) {
        if (!own.has(edgeKey(a, b))) continue;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
      }
      for (const [a, b] of contourEdges) {
        if (!own.has(edgeKey(a, b))) continue;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
      }
      ctx.restore();
    };
    const zc = getZoneColors(state.isDarkMode ?? false);
    highlight(entry, zc.entryRing);
    highlight(exit, zc.exitRing);
  }

  /**
   * Markers, drawn LAST: ONE marker per end — a fixed-size badge just outside
   * the silhouette with a dashed leader into the room. The tinted,
   * colour-walled room is the exact target. (An in-cell glyph used to sit
   * next to the badge; two sizes of the same emoji 20 px apart read as
   * clutter, and on expert cells it was unreadable anyway.)
   */
  private drawZoneMarkers(state: VoronoiMazeState): void {
    const { entry, exit } = this.zoneRegions(state);
    const zc = getZoneColors(state.isDarkMode ?? false);
    if (entry) this.drawBadge(entry, MAZE_EMOJIS.entrance, zc.entryRing, state);
    if (exit) this.drawBadge(exit, MAZE_EMOJIS.exit, zc.exitRing, state);
  }

  private drawBadge(region: VoronoiRegion, emoji: string, color: string, state: VoronoiMazeState): void {
    const { ctx } = this;
    const b = computeBadgePlacement(region, state.mazeData.regions, state.mazeData.contour, state.canvasSize);
    // leader: dashed, thin, in the room colour — from the badge rim to the room
    const dx = b.anchor.x - b.x;
    const dy = b.anchor.y - b.y;
    const len = Math.hypot(dx, dy);
    if (len > b.radius + 4) {
      ctx.save();
      ctx.strokeStyle = color;
      ctx.globalAlpha = 0.85;
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 4]);
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(b.x + (dx / len) * b.radius, b.y + (dy / len) * b.radius);
      ctx.lineTo(b.anchor.x, b.anchor.y);
      ctx.stroke();
      ctx.restore();
    }
    // badge: surface-toned disc (never white — a yellow glyph vanished on it),
    // thick coloured rim, soft shadow, glyph ≈ the disc
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.35)';
    ctx.shadowBlur = 8;
    ctx.shadowOffsetY = 2;
    ctx.beginPath();
    ctx.arc(b.x, b.y, b.radius, 0, 2 * Math.PI);
    ctx.fillStyle = state.isDarkMode ? MAZE_COLORS.badgeFillDark : MAZE_COLORS.badgeFillLight;
    ctx.fill();
    ctx.shadowColor = 'transparent';
    ctx.lineWidth = 3.5;
    ctx.strokeStyle = color;
    ctx.stroke();
    ctx.font = `${Math.round(b.radius * 1.25)}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#000';
    ctx.fillText(emoji, b.x, b.y + 1);
    ctx.restore();
  }

  /**
   * Render the solution path by filling regions with a semi-transparent color.
   * Uses region IDs (not positions) to avoid drawing lines through walls.
   */
  renderSolutionPath(state: VoronoiMazeState, pathRegionIds: number[]): void {
    if (pathRegionIds.length === 0) return;

    const { ctx } = this;
    const regionById = new Map<number, VoronoiRegion>();
    for (const r of state.mazeData.regions) regionById.set(r.id, r);

    // Clip solution fill to contour so it doesn't bleed outside the silhouette
    const contour = state.mazeData.contour;
    if (contour && contour.length > 2) {
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(contour[0].x, contour[0].y);
      for (let i = 1; i < contour.length; i++) {
        ctx.lineTo(contour[i].x, contour[i].y);
      }
      ctx.closePath();
      ctx.clip();
    }

    // Fill each region in the solution path
    ctx.fillStyle = GAME_COLORS.success;
    ctx.globalAlpha = 0.2;

    for (const rid of pathRegionIds) {
      const region = regionById.get(rid);
      if (!region || region.vertices.length < 3) continue;

      ctx.beginPath();
      ctx.moveTo(region.vertices[0].x, region.vertices[0].y);
      for (let i = 1; i < region.vertices.length; i++) {
        ctx.lineTo(region.vertices[i].x, region.vertices[i].y);
      }
      ctx.closePath();
      ctx.fill();
    }

    ctx.globalAlpha = 1.0;

    if (contour && contour.length > 2) {
      ctx.restore();
    }
  }

  // ---------------------------------------------------------------------------
  // SVG export (for print / download)
  // ---------------------------------------------------------------------------

  /**
   * Convert the Voronoi maze to an SVG string for print/download.
   */
  static toSVG(
    state: VoronoiMazeState,
    options: {
      width: number;
      height: number;
      includePath?: boolean;
      includeZones?: boolean;
      isDarkMode?: boolean;
    },
  ): string {
    const darkMode = options.isDarkMode ?? state.isDarkMode ?? false;
    const wallColor = darkMode ? MAZE_COLORS.wallDark : MAZE_COLORS.wallLight;
    const bgColor = darkMode
      ? MAZE_COLORS.backgroundDark
      : MAZE_COLORS.backgroundLight;

    const { wallEdges, contourEdges } = classifyEdges(state.mazeData);

    const parts: string[] = [];

    const contour = state.mazeData.contour;
    const vw = state.canvasSize.width;
    const vh = state.canvasSize.height;

    parts.push(
      `<?xml version="1.0" encoding="UTF-8"?>`,
      `<svg xmlns="http://www.w3.org/2000/svg" width="${options.width}" height="${options.height}" viewBox="0 0 ${vw} ${vh}">`,
      `<rect width="${vw}" height="${vh}" fill="${bgColor}"/>`,
    );

    // Define clip path from contour (for walls) — NOT used for passages
    if (contour && contour.length > 2) {
      const contourD = `M ${contour.map((p) => `${p.x},${p.y}`).join(' L ')} Z`;
      parts.push(`<defs><clipPath id="maze-clip"><path d="${contourD}"/></clipPath></defs>`);
    }

    const clipAttr = contour && contour.length > 2 ? ' clip-path="url(#maze-clip)"' : '';

    // Entry / exit cell tints UNDER the walls (walls stay crisp, openings stay open)
    if (options.includeZones !== false) {
      const { mazeData } = state;
      const tintOf = (id: number, tint: string) => {
        const region = mazeData.regions.find((r) => r.id === id);
        if (!region || region.vertices.length < 3) return;
        const d = `M ${region.vertices.map((p) => `${p.x},${p.y}`).join(' L ')} Z`;
        parts.push(`<path d="${d}" fill="${tint}"${clipAttr}/>`);
      };
      const zc = getZoneColors(darkMode);
      tintOf(mazeData.entryRegionId, zc.entryTint);
      tintOf(mazeData.exitRegionId, zc.exitTint);
    }

    // Walls and contour edges inside clip
    parts.push(`<g${clipAttr}>`);

    // Contour boundary edges (with gaps at passages)
    parts.push(`<g stroke="${wallColor}" stroke-width="3" fill="none" stroke-linecap="round">`);
    for (const [v1, v2] of contourEdges) {
      parts.push(`<line x1="${v1.x}" y1="${v1.y}" x2="${v2.x}" y2="${v2.y}"/>`);
    }
    parts.push(`</g>`);

    // Internal walls
    parts.push(`<g stroke="${wallColor}" stroke-width="1.5" fill="none" stroke-linecap="round">`);
    for (const [v1, v2] of wallEdges) {
      parts.push(`<line x1="${v1.x}" y1="${v1.y}" x2="${v2.x}" y2="${v2.y}"/>`);
    }
    parts.push(`</g>`);

    // Entry / exit ROOM walls in the room colour (only edges that exist → gaps stay gaps)
    if (options.includeZones !== false) {
      const { mazeData } = state;
      const highlight = (id: number, color: string) => {
        const region = mazeData.regions.find((r) => r.id === id);
        if (!region || region.vertices.length < 3) return;
        const own = new Set<string>();
        const v = region.vertices;
        for (let i = 0; i < v.length; i++) own.add(edgeKey(v[i], v[(i + 1) % v.length]));
        parts.push(`<g stroke="${color}" stroke-width="4" fill="none" stroke-linecap="round">`);
        for (const [a, b] of [...wallEdges, ...contourEdges]) {
          if (own.has(edgeKey(a, b))) parts.push(`<line x1="${a.x}" y1="${a.y}" x2="${b.x}" y2="${b.y}"/>`);
        }
        parts.push(`</g>`);
      };
      const zcH = getZoneColors(darkMode);
      highlight(mazeData.entryRegionId, zcH.entryRing);
      highlight(mazeData.exitRegionId, zcH.exitRing);
    }
    parts.push(`</g>`); // close clip group

    // Even-odd mask: fill everything outside contour with background
    if (contour && contour.length > 2) {
      const contourD = `M ${contour.map((p) => `${p.x},${p.y}`).join(' L ')} Z`;
      parts.push(`<path d="M 0,0 L ${vw},0 L ${vw},${vh} L 0,${vh} Z ${contourD}" fill="${bgColor}" fill-rule="evenodd"/>`);
    }

    // Contour outline on top
    if (contour && contour.length > 2) {
      const contourD = `M ${contour.map((p) => `${p.x},${p.y}`).join(' L ')} Z`;
      parts.push(`<path d="${contourD}" stroke="${wallColor}" stroke-width="3" fill="none" stroke-linecap="round" stroke-linejoin="round"/>`);
    }

    // Path
    if (options.includePath !== false && state.pathSegments.length > 0) {
      parts.push(
        `<g stroke="${GAME_COLORS.blue}" stroke-width="3" fill="none" stroke-linecap="round" stroke-linejoin="round" opacity="0.6">`,
      );
      for (const segment of state.pathSegments) {
        if (segment.points.length < 2) continue;
        let d = `M ${segment.points[0].x},${segment.points[0].y}`;
        for (let i = 1; i < segment.points.length; i++) {
          d += ` L ${segment.points[i].x},${segment.points[i].y}`;
        }
        parts.push(`<path d="${d}"/>`);
      }
      parts.push(`</g>`);
    }

    // Zone emojis
    if (options.includeZones !== false) {
      const { mazeData } = state;
      const regions = mazeData.regions;

      const entryRegion = regions.find(
        (r) => r.id === mazeData.entryRegionId,
      );
      const exitRegion = regions.find(
        (r) => r.id === mazeData.exitRegionId,
      );

      const badge = (region: VoronoiRegion, emoji: string, color: string) => {
        const b = computeBadgePlacement(region, regions, mazeData.contour, state.canvasSize);
        const dx = b.anchor.x - b.x;
        const dy = b.anchor.y - b.y;
        const len = Math.hypot(dx, dy);
        if (len > b.radius + 4) {
          parts.push(
            `<line x1="${b.x + (dx / len) * b.radius}" y1="${b.y + (dy / len) * b.radius}" x2="${b.anchor.x}" y2="${b.anchor.y}" stroke="${color}" stroke-width="2" stroke-dasharray="5 4" stroke-linecap="round" opacity="0.85"/>`,
          );
        }
        parts.push(
          `<circle cx="${b.x}" cy="${b.y}" r="${b.radius}" fill="${darkMode ? MAZE_COLORS.badgeFillDark : MAZE_COLORS.badgeFillLight}" stroke="${color}" stroke-width="3.5"/>`,
          `<text x="${b.x}" y="${b.y + 1}" text-anchor="middle" dominant-baseline="middle" font-size="${Math.round(b.radius * 1.25)}" font-family="Arial">${emoji}</text>`,
        );
      };
      const zcB = getZoneColors(darkMode);
      if (entryRegion) badge(entryRegion, MAZE_EMOJIS.entrance, zcB.entryRing);
      if (exitRegion) badge(exitRegion, MAZE_EMOJIS.exit, zcB.exitRing);
    }

    parts.push(`</svg>`);
    return parts.join('\n');
  }
}
